import React, { useState, useRef, useMemo } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { Link, useNavigate } from "react-router-dom";
import { FaEdit, FaTrash } from 'react-icons/fa';
import { toast } from 'react-toastify';
import Button from 'react-bootstrap/Button';
import classnames from 'classnames';
import Modal from 'react-bootstrap/Modal';
import Server from "../../Shared/Server";
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';

const emptyCellFormatter = (params) =>
  params.value === null || params.value === undefined || params.value === '' ? '-' : params.value;

const ServerSideCustomers = () => {
  const gridApi = useRef();
  const gridRef = useRef();
  const [show, setShow] = useState(false);
  const [publicID, setPublicID] = useState();
  const [isDeleting, setIsDeleting] = useState(false);
  const navigate = useNavigate();

  const columnDefs = useMemo(() => [
    {
      headerName: "#",
      filter: false,
      sortable: false,
      maxWidth: 70,
      valueFormatter: (params) => (params.node.rowIndex + 1).toString(),
    },
    {
      headerName: "Actions",
      minWidth: 140,
      pinned: 'left',
      sortable: false,
      filter: false,
      cellRenderer: (e) => {
        const { data } = e;
        if (!data) return null;
        const hasLocations = data.locations > 0;
        return (
          <div className="d-flex gap-1">
            <Link
              className="btn btn-outline-info"
              to="/update-customer"
              state={{ customer: data }}
              title="Edit"
            >
              <FaEdit />
            </Link>
            <button
              className={classnames('btn btn-outline-danger', { disabled: hasLocations })}
              onClick={() => !hasLocations && handleShow(data.public_id)}
              title={hasLocations ? "Cannot delete (has locations)" : "Delete"}
            >
              <FaTrash />
            </button>
          </div>
        );
      },
    },
    { headerName: "Name", field: "full_name", minWidth: 220 },
    { headerName: "Contact", field: "contact_number" },
    { headerName: "Locations", field: "locations" },
    { headerName: "R.Name", field: "responsible_name" },
    { headerName: "R.Contact", field: "responsible_contact" },
  ], []);

  const defaultColDef = useMemo(() => ({
    sortable: false,
    filter: true,
    resizable: true,
    valueFormatter: emptyCellFormatter,
    filterParams: {
      buttons: []
    }
  }), []);

  const fetchData = async (params) => {
    const dataColumns = columnDefs.filter(col => col.field);
    const requestParams = {
      draw: 1,
      start: params.startRow,
      length: params.endRow - params.startRow,
      columns: dataColumns.map(col => ({
        data: col.field,
        searchable: col.filter !== false,
        orderable: !!col.sortable,
        search: { value: '', regex: false }
      })),
      order: params.sortModel.map(sort => ({
        column: dataColumns.findIndex(col => col.field === sort.colId),
        dir: sort.sort
      })),
      search: Object.keys(params.filterModel).map(key => ({
        col: key,
        value: params.filterModel[key].filter?.toString().toLowerCase() ?? '',
        type: params.filterModel[key].type || "contains"
      })),
      filter: {}
    };

    try {
      const response = await Server.post('/customers/serverside', requestParams);
      params.successCallback(response.data.data, response.data.recordsFiltered);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to load customers');
      params.failCallback();
    }
  };

  const onGridReady = (params) => {
    gridApi.current = params.api;
    params.api.updateGridOptions({
      datasource: {
        getRows: (p) => fetchData(p)
      }
    });
  };

  const onFirstDataRendered = (params) => {
    params.api.autoSizeAllColumns();
  };

  const onGridSizeChanged = (params) => {
    params.api.autoSizeAllColumns();
  };

  const handleClose = () => {
    if (isDeleting) return;
    setShow(false);
  };

  const handleShow = (public_id) => {
    setShow(true);
    setPublicID(public_id);
  };

  const handleDelete = async (public_id) => {
    setIsDeleting(true);
    try {
      const response = await Server.delete(`/customers/${public_id}`);
      toast.success(response.data.detail);
      gridApi.current?.purgeInfiniteCache();
      setShow(false);
    } catch (error) {
      if (error?.response?.status === 401) {
        navigate('/login');
      } else if (error?.response?.status === 403) {
        toast.error(error.response.data.detail);
        navigate('/');
      } else {
        toast.error(error?.response?.data?.detail || 'Failed to delete customer');
      }
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <>
      <div className="row">
        <div className="col-md-12">
          <Link to="/add-customer" className="btn btn-outline-info mb-2 float-end">
            Add Customer
          </Link>
        </div>
      </div>

      <div className="card">
        <div className="card-header p-2 bg-info text-white d-flex justify-content-center">
          <h3 className="card-title mb-0">Customers</h3>
        </div>
        <div className="card-body">
          <div className="ag-theme-alpine responsive-aggrid" style={{ height: 400, width: '100%' }}>
            <AgGridReact
              ref={gridRef}
              rowModelType="infinite"
              cacheBlockSize={100}
              paginationPageSize={100}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              rowSelection="multiple"
              enableCellTextSelection={true}
              suppressCellFocus={true}
              onGridReady={onGridReady}
              onFirstDataRendered={onFirstDataRendered}
              onGridSizeChanged={onGridSizeChanged}
            />
          </div>
        </div>
      </div>

      <Modal show={show} onHide={handleClose} backdrop={isDeleting ? 'static' : true}>
        <Modal.Header closeButton={!isDeleting}>
          <Modal.Title>Delete Customer</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete this customer? This action cannot be undone.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose} disabled={isDeleting}>
            No
          </Button>
          <Button variant="danger" onClick={() => handleDelete(publicID)} disabled={isDeleting}>
            {isDeleting ? 'Deleting…' : 'Yes, delete'}
          </Button>
        </Modal.Footer>
      </Modal>
      <br />
    </>
  );
};

export default ServerSideCustomers;
